const MIN_FONT_SIZE = 4;
const MAX_FONT_SIZE = 32;
const VALUE_RATIO = 0.9;
const SUBTEXT_RATIO = 0.75;
const LINE_HEIGHT = 1.25;
const PADDING = 3;
const DEFAULT_FONT_FAMILY = [
  'ui-sans-serif',
  'system-ui',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif'
].join(', ');

let measureContext = null;

function fontSizeForWidth(text, maxWidth, fontWeight, fontFamily) {
  if (!text || maxWidth <= 0) return 0;
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  if (!measureContext) return 0;

  let size = MIN_FONT_SIZE;
  while (size <= MAX_FONT_SIZE) {
    measureContext.font = `${fontWeight} ${size}px ${fontFamily}`;
    if (measureContext.measureText(text).width > maxWidth) {
      return size > MIN_FONT_SIZE ? size - 1 : 0;
    }
    size += 1;
  }
  return MAX_FONT_SIZE;
}

let escapeElement = null;

function escapeHtml(text) {
  if (!escapeElement) escapeElement = document.createElement('div');
  escapeElement.textContent = String(text);
  return escapeElement.innerHTML;
}

// Largest single-line title size that fits the width. When there is no title
// but a value, size the title slot to the value instead so it fills the cell.
function titleSizeForWidth(name, value, availWidth, fontFamily) {
  const byWidth = fontSizeForWidth(name, availWidth, '600', fontFamily);
  let size = byWidth === 0 ? MIN_FONT_SIZE : byWidth;

  if (!name && value) {
    const valueFit = fontSizeForWidth(value, availWidth, '700', fontFamily);
    if (valueFit > 0) size = Math.round(valueFit / VALUE_RATIO);
  }

  return { size, needsWrap: byWidth === 0 };
}

function fitToHeight(availHeight, value, subtext) {
  const sizeFor = (multiplier) => Math.floor(availHeight / (multiplier * LINE_HEIGHT));

  let keptValue = value;
  let keptSubtext = subtext;
  let maxByHeight = sizeFor(1 + (keptValue ? VALUE_RATIO : 0) + (keptSubtext ? SUBTEXT_RATIO : 0));

  if (maxByHeight < MIN_FONT_SIZE && keptSubtext) {
    keptSubtext = '';
    maxByHeight = sizeFor(1 + (keptValue ? VALUE_RATIO : 0));
  }
  if (maxByHeight < MIN_FONT_SIZE && keptValue) {
    keptValue = '';
    maxByHeight = sizeFor(1);
  }

  return { value: keptValue, subtext: keptSubtext, maxByHeight };
}

function labelMarkup(config) {
  const {
    availWidth,
    color,
    fontFamily,
    name,
    value,
    subtext,
    titleSize,
    valueSize,
    subtextSize,
    titleNeedsWrap
  } = config;

  const wrap = titleNeedsWrap ? 'white-space:normal;word-break:break-word;' : '';
  const clamp = 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;';

  const parts = [
    `<div style="width:${availWidth}px;text-align:center;color:${color};line-height:1.15;font-family:${fontFamily}">`,
    `<div style="font-weight:600;font-size:${titleSize}px;${clamp}${wrap}">${escapeHtml(name)}</div>`
  ];
  if (value) {
    parts.push(`<div style="font-weight:700;font-size:${valueSize}px">${escapeHtml(value)}</div>`);
  }
  if (subtext) {
    parts.push(`<div style="font-weight:600;font-size:${subtextSize}px">${escapeHtml(subtext)}</div>`);
  }
  parts.push('</div>');

  return parts.join('');
}

export function adaptiveTreemapLabel(point, options = {}) {
  if (!point.shapeArgs) return '';

  const fontFamily = options.fontFamily || DEFAULT_FONT_FAMILY;
  const availWidth = Math.max(1, point.shapeArgs.width - PADDING * 2);
  const availHeight = Math.max(1, point.shapeArgs.height - PADDING * 2);

  const title = point.name || '';
  const value = point.value || '';
  const subtext = point.subtext || '';

  const titleFit = titleSizeForWidth(title, value, availWidth, fontFamily);
  const fitted = fitToHeight(availHeight, value, subtext);

  const titleSize = Math.max(MIN_FONT_SIZE, Math.min(titleFit.size, fitted.maxByHeight));

  return labelMarkup({
    availWidth,
    color: options.color || '#ffffff',
    fontFamily,
    name: title,
    value: fitted.value,
    subtext: fitted.subtext,
    titleSize,
    valueSize: Math.max(MIN_FONT_SIZE, Math.round(titleSize * VALUE_RATIO)),
    subtextSize: Math.max(MIN_FONT_SIZE, Math.round(titleSize * SUBTEXT_RATIO)),
    titleNeedsWrap: titleFit.needsWrap
  });
}
